import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

/**
 * @typedef {object} PastryVariantPickerRefs
 * @property {HTMLElement} variantGrid - The variant cards grid container.
 * @property {HTMLElement[]} variantCards - Array of variant card elements.
 * @property {HTMLElement[]} qtyValues - Array of quantity display elements.
 * @property {HTMLSelectElement} pickupSelect - The pickup day dropdown.
 * @property {HTMLInputElement} pickupWeek - Hidden input for pickup week tracking.
 * @property {HTMLScriptElement} variantData - JSON script with variant data.
 */

/**
 * Pastry variant picker component — renders variant cards with quantity selectors
 * and a pickup day dropdown with Wednesday midnight cutoff enforcement.
 * Supports multi-variant selection: customers can order different box types at once.
 * @extends Component<PastryVariantPickerRefs>
 */
class PastryVariantPicker extends Component {
  /** @type {{ id: number, title: string, price: number, compare_at_price: number, available: boolean }[]} */
  #variants = [];

  /** @type {number[]} Quantity for each variant by index */
  #quantities = [];

  /** @type {string} */
  #selectedOption = '';

  /** @type {string} IANA timezone for cutoff */
  #timezone = 'America/Denver';

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Parse variant data
    try {
      this.#variants = JSON.parse(this.refs.variantData.textContent ?? '[]');
    } catch {
      this.#variants = [];
    }

    // Initialize quantities — first variant starts at 1, others at 0
    this.#quantities = this.#variants.map((_, i) => (i === 0 ? 1 : 0));
    //this.#quantities = this.#variants.map((_, i) => (0));
    

    // Read timezone from data attribute
    const tz = /** @type {HTMLElement|null} */ (this.closest('[data-timezone]'))?.dataset.timezone;
    if (tz) this.#timezone = tz;

    // Populate options
    this.#populateOptions();

    // Populate pickup options
    this.#populatePickupOptions();

    // Update price display
    this.#updatePriceDisplay();

    // Intercept the product form submission to handle multi-variant add
    this.#interceptFormSubmit();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Increase quantity for a variant card.
   * @param {number} index - Variant index
   * @param {Event} _event
   */
  increaseQuantity(index, _event) {
    const card = _event.target.closest('.pastry-card');
    if (card.classList.contains('disabled'))
      return;
    const idx = Number(index);
    if (idx < 0 || idx >= this.#quantities.length) return;

    /** @type {number} */
    const current = this.#quantities[idx] ?? 0;
    this.#quantities[idx] = current + 1;

    this.#renderQuantities();
    this.#updatePriceDisplay();
  }

  /**
   * Decrease quantity for a variant card. Minimum is 0.
   * @param {number} index - Variant index
   * @param {Event} _event
   */
  decreaseQuantity(index, _event) {
    const card = _event.target.closest('.pastry-card');
    if (card.classList.contains('disabled'))
      return;
    const idx = Number(index);
    if (idx < 0 || idx >= this.#quantities.length) return;

    const newQty = (this.#quantities[idx] ?? 0) - 1;

    // Prevent removing the last selected variant — at least one must have qty >= 1
    if (newQty <= 0) {
      const othersSelected = this.#quantities.some((q, i) => i !== idx && q > 0);
      //if (!othersSelected) return;
    }

    this.#quantities[idx] = Math.max(0, newQty);

    this.#renderQuantities();
    this.#updatePriceDisplay();
  }

  /**
   * Handle pickup dropdown change.
   */
  onPickupChange() {
    const select = this.refs.pickupSelect;
    const option = /** @type {HTMLOptionElement} */ (select.options[select.selectedIndex]);
    if (option) {
      this.refs.pickupWeek.value = option.dataset.week || '';
    }
  }

  // ── Private methods ──────────────────────────────────────────

  /**
   * Intercept the buy-buttons form submit to handle multi-variant cart add.
   * Removes the standard on:submit handler from product-form-component so the
   * Component event delegation doesn't trigger the single-variant handleSubmit.
   */
  #interceptFormSubmit() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    // Remove the standard submit handler so it doesn't compete with ours
    const productFormComponent = section.querySelector('product-form-component');
    if (productFormComponent) {
      productFormComponent.removeAttribute('on:submit');
    }

    const form = /** @type {HTMLFormElement|null} */ (section.querySelector('form[data-type="add-to-cart-form"]'));
    if (!form) return;

    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.#handleMultiAdd(form);
      },
      { signal: this.#abortController.signal }
    );
  }

  /**
   * Add multiple variant line items to the cart at once.
   * @param {HTMLFormElement} form
   */
  async #handleMultiAdd(form) {
    // Validate: at least one variant selected
    const items = this.#getSelectedItems();
    if (items.length === 0) return;
    console.log(items);
    let cartItems;
    // Validate: pickup day selected
    const pickupSelect = this.refs.pickupSelect;
    if (pickupSelect && this.#selectedOption == 'pickup') {
      if (!pickupSelect.value) {
        pickupSelect.reportValidity();
        return;
      }

      // Get pickup properties
      const pickupDay = pickupSelect.value;
      const pickupWeek = this.refs.pickupWeek.value;

      // Build items array with pickup properties on each line item
      cartItems = items.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        properties: {
          'Pickup Day': pickupDay,
          '_Pickup Week': pickupWeek,
        },
      }));
    } else {
      cartItems = items.map((item) => ({
        id: item.id,
        quantity: item.quantity
      }));
    }

    // Collect cart section IDs for morphing
    /** @type {string[]} */
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    cartPerformance.createStartingMarker('add:user-action');

    // Find and disable the add-to-cart button
    const section = this.closest('.shopify-section');
    const addToCartBtn = /** @type {HTMLButtonElement|null} */ (section?.querySelector('[ref="addToCartButton"]'));
    if (addToCartBtn) addToCartBtn.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: cartItems,
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) {
        // Error
        console.error('Cart add error:', data.message);
        return;
      }

      // Success — dispatch CartAddEvent so cart drawer opens and updates
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      const productFormComponent = /** @type {HTMLElement|null} */ (section?.querySelector('product-form-component'));

      if (productFormComponent) {
        productFormComponent.dispatchEvent(
          new CartAddEvent({}, (items[0]?.id ?? 0).toString(), {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: productFormComponent.dataset.productId ?? '',
            sections: data.sections,
          })
        );
      }

      // Animate the add-to-cart button
      const addToCartComponent = /** @type {any} */ (section?.querySelector('add-to-cart-component'));
      if (addToCartComponent?.animateAddToCart) {
        addToCartComponent.animateAddToCart();
      }
    } catch (error) {
      console.error('Cart add failed:', error);
    } finally {
      if (addToCartBtn) addToCartBtn.disabled = false;
      cartPerformance.measureFromEvent('add:user-action', new Event('submit'));
    }
  }

  /**
   * Get all variants with quantity > 0.
   * @returns {{ id: number, quantity: number, price: number, compare_at_price: number }[]}
   */
  #getSelectedItems() {
    const items = [];
    for (let i = 0; i < this.#variants.length; i++) {
      const qty = this.#quantities[i] ?? 0;
      const variant = this.#variants[i];
      if (qty > 0 && variant) {
        items.push({
          id: variant.id,
          quantity: qty,
          price: variant.price,
          compare_at_price: variant.compare_at_price || 0,
        });
      }
    }
    return items;
  }

  /**
   * Render quantity values and selected card state.
   */
  #renderQuantities() {
    const qtyEls = this.refs.qtyValues;

    for (let i = 0; i < this.#quantities.length; i++) {
      const el = qtyEls[i];
      if (el) el.textContent = (this.#quantities[i] ?? 0).toString();

    }
  }

  /**
   * Update the price display on the product page.
   * Shows the total price across all selected variants, with sale pricing if applicable.
   */
  #updatePriceDisplay() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const priceContainer = section.querySelector('product-price [ref="priceContainer"]');
    if (!priceContainer) return;

    const items = this.#getSelectedItems();

    // Calculate totals across all selections
    const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const totalCompareAt = items.reduce((sum, item) => {
      const compareAt = item.compare_at_price > item.price ? item.compare_at_price : item.price;
      return sum + compareAt * item.quantity;
    }, 0);

    const displayPrice = totalPrice > 0 ? totalPrice : (this.#variants[0]?.price || 0);
    const hasDiscount = totalPrice > 0 && totalCompareAt > totalPrice;

    if (hasDiscount) {
      priceContainer.innerHTML =
        `<span role="group" style="margin-right: 4px;">` +
        `<span class="visually-hidden">Regular price</span>` +
        `<span class="compare-at-price">${this.#formatMoney(totalCompareAt)}</span>` +
        `</span>` +
        `<span role="group">` +
        `<span class="visually-hidden">Sale price</span>` +
        `<span class="price">${this.#formatMoney(displayPrice)}</span>` +
        `</span>`;
    } else {
      priceContainer.innerHTML = `<span class="price">${this.#formatMoney(displayPrice)}</span>`;
    }
  }

  /**
   * Format cents to currency string.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  /**
   * Populate the delivery and pickup date options
   */
  #populateOptions() {
    const shippingOptions = this.querySelector('.shipping-options');
    const select = this.refs.pickupSelect;
    const _this = this;
    
    if (shippingOptions instanceof HTMLElement) {
      shippingOptions.querySelectorAll('.shipping-option_header').forEach((el) => {
        if (el instanceof HTMLElement) {
          const input = el.querySelector('input');
          if (input.checked) {
            this.#selectedOption = input.value;
          }
          console.log(this.#selectedOption);
          el.addEventListener('click', function(){
            _this.#selectedOption = input.value;
            if (input.value == 'delivery') {
              select.disabled = true;
            } else {
              select.disabled = false;
            }
            console.log(_this.#selectedOption);
          });
        }
      });

      if (this.#selectedOption == 'delivery') {
        select.disabled = true;
      }
    }
  }
  /**
   * Populate the pickup dropdown with available Friday and Saturday dates.
   * Enforces Wednesday 10:00 PM cutoff.
   */
  #populatePickupOptions() {
    const select = this.refs.pickupSelect;
    if (!select) return;

    const now = this.#getNowInTimezone();
    const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const hour = now.getHours();
    const minute = now.getMinutes();

      // Determine if we've passed Wednesday 10:00 PM cutoff.
      // Wednesday = 3. Once it's Thursday (4+), cutoff has passed.
      // On Wednesday itself, cutoff is at 10:00 PM (22:00) or later.
      const isPastCutoff =
        dayOfWeek > 3 || // Thursday through Saturday
        (dayOfWeek === 3 && hour >= 22); // Wed 10:00 PM or later

    let baseFriday;

    if (isPastCutoff) {
      // Orders go to NEXT week — find next week's Friday
      const daysUntilNextFriday = ((5 - dayOfWeek + 7) % 7) || 7;
      baseFriday = new Date(now);
      baseFriday.setDate(now.getDate() + daysUntilNextFriday);
      if (daysUntilNextFriday <= 2) {
        baseFriday.setDate(baseFriday.getDate() + 7);
      }
    } else {
      // Orders go to THIS week — find this week's Friday
      const daysUntilFriday = ((5 - dayOfWeek + 7) % 7) || 7;
      baseFriday = new Date(now);
      baseFriday.setDate(now.getDate() + daysUntilFriday);
    }

    // Saturday is Friday + 1
    const baseSaturday = new Date(baseFriday);
    baseSaturday.setDate(baseFriday.getDate() + 1);

    // Format dates
    const fridayLabel = this.#formatPickupDate(baseFriday);
    const saturdayLabel = this.#formatPickupDate(baseSaturday);

    // Calculate week identifier
    const weekId = this.#getWeekId(baseFriday);

    // Clear existing options except the placeholder
    while (select.options.length > 1) {
      select.remove(1);
    }

    /*
    // Add Friday option
    const friOpt = document.createElement('option');
    friOpt.value = `Friday, ${fridayLabel} — 10 AM to 12 PM`;
    friOpt.textContent = `Friday, ${fridayLabel} — 10 AM to 12 PM`;
    friOpt.dataset.week = weekId;
    select.appendChild(friOpt);
    */
    // Add Friday option
    const times = ['10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM'];
    times.map((time) => {
      let friOpt = document.createElement('option');
      friOpt.value = `Friday, ${fridayLabel} — ${time}`;
      friOpt.textContent = `Friday, ${fridayLabel} — ${time}`;
      friOpt.dataset.week = weekId;
      select.appendChild(friOpt);
    });

    /*
    // Add Saturday option
    const satOpt = document.createElement('option');
    satOpt.value = `Saturday, ${saturdayLabel} — 10 AM to 12 PM`;
    satOpt.textContent = `Saturday, ${saturdayLabel} — 10 AM to 12 PM`;
    satOpt.dataset.week = weekId;
    select.appendChild(satOpt);
    */
    // Add Saturday option
    times.map((time) => {
      let satOpt = document.createElement('option');
      satOpt.value = `Saturday, ${saturdayLabel} — ${time}`;
      satOpt.textContent = `Saturday, ${saturdayLabel} — ${time}`;
      satOpt.dataset.week = weekId;
      select.appendChild(satOpt);
    });
  }

  /**
   * Get the current date/time in the store's timezone.
   * @returns {Date}
   */
  #getNowInTimezone() {
    const now = new Date();
    const tzString = now.toLocaleString('en-US', { timeZone: this.#timezone });
    return new Date(tzString);
  }

  /**
   * Format a date for the pickup option label.
   * @param {Date} date
   * @returns {string} e.g. "April 11"
   */
  #formatPickupDate(date) {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    });
  }

  /**
   * Get a week identifier string from a date.
   * @param {Date} date
   * @returns {string} e.g. "2026-W15"
   */
  #getWeekId(date) {
    const year = date.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    const diff = date.getTime() - startOfYear.getTime();
    const oneWeek = 604800000;
    const weekNum = Math.ceil((diff / oneWeek + startOfYear.getDay() + 1) / 7);
    return `${year}-W${String(weekNum).padStart(2, '0')}`;
  }
}

customElements.define('pastry-variant-picker-component', PastryVariantPicker);
